import StateObserver, { now, user } from 'src/StateObserver';
import {
  trackSessionStateSave,
  trackSessionStateLoad,
} from 'src/game/crossplay/crossplay.tracking';
import type { CrossPlayStartParams } from 'src/game/crossplay/crossplay.types';
import { startGemsPinchSequence } from 'src/game/shop/Helpers';
import { track } from 'src/lib/analytics/utils';
import { GCInstant } from 'src/lib/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { withLoading } from 'src/lib/utils';
import { magicLampsAssets } from 'src/loadingGroups';
import { Feature } from 'src/replicant/analytics';
import * as Inventory from 'src/replicant/inventory';
import {
  magicLampsGetContinueCost,
  magicLampsGetCurrentRound,
  magicLampsGetCurrentStage,
  magicLampsGetEntryCost,
  magicLampsGetLastViewedAt,
  magicLampsGetRewardSet,
  magicLampsIsAvailable,
} from 'src/replicant/match2/magiclamps/magiclamps.getters';
import { getMatch2RewardGemValue } from 'src/replicant/match2/match2.getters';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { FixedGemsReward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';

/**
 * Open magic lamps popup if it is active, on launch sequence.
 */
export async function magicLampsLaunch() {
  // Skip if the feature is not available
  if (!magicLampsIsAvailable(user(), now())) return;

  // Open magic lamps popup
  await magicLampsOpen('launch');
}

export async function magicLampsPostGame() {
  // Skip if the feature is not available
  if (!magicLampsIsAvailable(user(), now())) return;

  // Get last time user has seen the magic lamps popup
  const lastViewedAt = magicLampsGetLastViewedAt(user());

  // Skip if user has seen the popup before
  if (lastViewedAt) return;

  // Open magic lamps popup
  await magicLampsOpen('postLevel');
}

/**
 * Open the main magic lamps popup.
 */
export async function magicLampsOpen(origin: PopupDialogOrigin | undefined) {
  // Skip if not active
  if (!magicLampsIsAvailable(user(), now())) return;

  // Wait for magic lamps assets to load
  await withLoading(() => magicLampsAssets.load(), {
    feature: Feature.MAGIC_LAMPS._,
    subFeature: Feature.MAGIC_LAMPS.ASSETS,
  });

  // Update last viewed timestamp for magic lamps popup
  void StateObserver.invoke.magicLampsView();

  // Open magic lamps popup
  await openPopupPromise('popupMatch2MagicLamps', { origin });

  // Any display satisfies the recurring promo for its current event.
  await resolvePromoGroupFeature({
    groupId: 'recurringFeatures',
    itemId: 'magicLamps',
  });

  // Collect rewards if any
  await magicLampsCollectRewards(origin);
}

/**
 * Start magic lamps gameplay, if user has enough gems, otherwise prompt to buy.
 */
export async function magicLampsPlay() {
  const entryCost = magicLampsGetEntryCost(user());
  const currentGems = Inventory.getters.getGems(user());

  if (currentGems < entryCost) {
    // Not enough gems, prompt user to buy some
    const result = await startGemsPinchSequence({
      feature: 'match2',
      pinch: 'gems',
      gemsNeeded: entryCost - currentGems,
      crossplayTracking: {
        feature: 'magicLamps',
        subFeature: `magicLamps_play`,
      },
    });

    // Skip if user has not bought any gems
    if (result !== 'success') return false;
  }

  // Try to start the gameplay - if user doesn't have enough gems, it will fail
  const result = await StateObserver.invoke.magicLampsPlay();

  // Send analytics event.
  track('MagicLampStart', {
    isFirstofEvent: user().match2.magicLamps?.numPlays === 1,
  });

  return result;
}

/**
 * Continue magic lamps gameplay, if user has enough gems, otherwise prompt to buy.
 */
export async function magicLampsContinue() {
  const continueCost = magicLampsGetContinueCost(user());
  const currentGems = Inventory.getters.getGems(user());

  if (currentGems < continueCost) {
    trackSessionStateSave({
      origin: 'magicLamps',
      feature: 'magicLamps',
      subFeature: 'magicLamps_continue',
      timestamp: now(),
    });

    // Not enough gems, prompt user to buy some
    const result = await startGemsPinchSequence({
      feature: 'match2',
      pinch: 'gems',
      gemsNeeded: continueCost - currentGems,
      crossplayTracking: {
        feature: 'magicLamps',
        subFeature: `magicLamps_continue`,
        continueCount: user().match2.magicLamps?.continuesUsed ?? 0,
      },
    });

    // Skip if user has not bought any gems
    if (result !== 'success') return false;
  }

  // Try to continue the gameplay - if user doesn't have enough gems, it will fail
  const result = await StateObserver.invoke.magicLampsContinue();

  // Send analytics event.
  const { magicLamps } = user().match2;
  track('MagicLampContinue', {
    continueCount: magicLamps?.continuesUsed,
    stage: magicLampsGetCurrentStage(user()),
    round: magicLampsGetCurrentRound(user()),
    rewardGems: magicLamps?.rewards.gems ?? 0,
    rewardGemsConverted: getMatch2RewardGemValue(
      user(),
      processMatch2Rewards(magicLampsGetRewardSet(user()), now()),
    ),
  });

  return result;
}

/**
 * Prompt user to quit magic lamps, with no options to collect accumulated rewards
 */
export async function magicLampsPromptQuit() {
  const carryOn = await openPopupPromise('popupConfirmation', {
    title: i18n('match2.magicLamps.promptQuitTitle'),
    message: i18n('match2.magicLamps.promptQuitMessage'),
    buttonOkay: i18n('match2.magicLamps.promptQuitCancel'),
    buttonCancel: i18n('match2.magicLamps.promptQuitOkay'),
  });

  return !carryOn;
}

/**
 * Prompt user to collect accumulated rewards and quit magic lamps.
 */
export async function magicLampsPromptCollect() {
  return await openPopupPromise('popupMatch2MagicLampsCollect', {});
}

/**
 * Collect magic lamps accumulated rewards.
 */
export async function magicLampsCollectRewards(
  origin: PopupDialogOrigin | undefined = undefined,
) {
  const rewards = magicLampsGetRewardSet(user());

  // Skip if there is no rewards to collect
  if (!rewards.length) return false;

  // Grant rewards available
  const collected = await StateObserver.invoke.magicLampsCollectRewards();
  const processed = processMatch2Rewards(rewards, now());

  // Send analytics event.
  const { magicLamps } = user().match2;
  const fixedGemsReward = processed.find(
    (x) => x.type === 'gems',
  ) as FixedGemsReward;

  track('MagicLampExit', {
    continueCount: magicLamps?.continuesUsed,
    withRewards: collected,
    stage: magicLampsGetCurrentStage(user()),
    round: magicLampsGetCurrentRound(user()),
    rewardGems: fixedGemsReward?.amount ?? 0,
    rewardGemsConverted: getMatch2RewardGemValue(user(), processed),
  });

  // Skip if user has not collected any rewards
  if (!collected) return false;

  // Show the rewards popup
  return await openPopupPromise('popupMatch2MagicLampsRewards', {
    rewards,
    origin,
  });
}

/**
 * Resume magic lamps gameplay.
 */
export async function magicLampsResume(origin: PopupDialogOrigin | undefined) {
  // Check if user has an ongoing magic lamps gameplay.
  if (!user().match2.magicLamps) return false;

  const crossplayTracking =
    GCInstant.entryData.crossplayTracking ?? ({} as CrossPlayStartParams);

  trackSessionStateLoad(crossplayTracking);

  // Resume magic lamps gameplay.
  await magicLampsOpen(origin);

  // Break the sequence.
  return true;
}
